/**
 * packages/dados/src/conexao.ts — estado de conexão e de salvamento, observável.
 *
 * REGRA QUE GOVERNA ESTE ARQUIVO (decisão de 26/07/2026): o motor roda no navegador,
 * então perder a conexão NÃO para o trabalho. Este módulo **nunca bloqueia a edição**
 * e não tem função que impeça nada — ele só informa. As três coisas que ele faz:
 *
 *   1. dizer se dá para falar com o servidor;
 *   2. dizer se o que o usuário digitou já subiu;
 *   3. avisar quem sabe subir o rascunho, assim que a conexão voltar.
 *
 * POR QUE NÃO CONFIAR SÓ EM `navigator.onLine`: ele responde "tem placa de rede com
 * link", não "o Supabase responde". Wi-Fi de hotel com portal cativo, VPN caída, DNS
 * quebrado e projeto fora do ar dão `onLine === true` com toda chamada falhando. A
 * fonte de verdade aqui é o RESULTADO DAS CHAMADAS; `navigator` entra como sinal
 * auxiliar, e só na direção em que é confiável:
 *
 *   · evento `offline`  → confiável. Caiu mesmo. Marca offline na hora.
 *   · evento `online`   → é só uma pista. Tenta ressincronizar; quem decide se
 *                         voltou é a chamada dar certo.
 *   · chamada falhou por rede/servidor → offline, mesmo com `onLine === true`.
 *   · chamada deu certo → online, mesmo com `onLine === false`.
 *
 * Sem DOM nos tipos deste projeto (`lib: ES2022`), os globais do navegador são lidos
 * de `globalThis` com verificação em tempo de execução — o que também deixa o módulo
 * seguro em Node (teste, SSR): lá não há eventos, e o estado só muda por chamada real.
 */

/* ============================================================================
 * Estado
 * ========================================================================== */

export type SituacaoRede = 'online' | 'offline';

/**
 * · `ocioso`   — nada para salvar.
 * · `salvando` — chamada em andamento.
 * · `salvo`    — o servidor confirmou.
 * · `pendente` — falhou por falta de conexão; vai ser tentado de novo sozinho.
 * · `erro`     — falhou por um motivo que repetir não resolve (permissão negada,
 *                conflito de versão). Exige decisão do usuário.
 */
export type SituacaoSalvamento = 'ocioso' | 'salvando' | 'salvo' | 'pendente' | 'erro';

export interface EstadoConexao {
  rede: SituacaoRede;
  salvamento: SituacaoSalvamento;
  /** Quantas alterações locais ainda não subiram (informado por quem edita). */
  pendencias: number;
  /** Último salvamento confirmado pelo servidor. */
  salvoEm: Date | null;
  /** Motivo do último `erro` (já em PT-BR). `null` quando não há. */
  erro: string | null;
  /** Frase pronta para a barra de status. Derivada dos campos acima. */
  mensagem: string;
}

export interface OpcoesMonitor {
  /**
   * Falhas de rede seguidas antes de declarar offline. 1 = reage na primeira
   * (padrão: uma chamada de escrita que não completa já é motivo para avisar).
   */
  falhasParaOffline?: number;
  /** Espera entre tentativas enquanto houver pendência, em ms. */
  atrasos?: readonly number[];
  /** Injeção para teste; padrão `() => new Date()`. */
  agora?: () => Date;
}

const ATRASOS_PADRAO = [5_000, 10_000, 20_000, 40_000, 60_000] as const;

/* ============================================================================
 * Classificação do erro — o coração da decisão "offline de verdade"
 * ========================================================================== */

function textoDoErro(erro: unknown): string {
  if (erro === null || erro === undefined) return '';
  if (typeof erro === 'string') return erro;
  if (erro instanceof Error) return `${erro.name}: ${erro.message}`;
  const o = erro as { message?: unknown; name?: unknown; code?: unknown };
  const partes = [o.name, o.code, o.message].filter((p) => typeof p === 'string');
  return partes.join(': ');
}

/** Marcas de falha de transporte deixadas por cada navegador/runtime. */
const MARCAS_DE_REDE = [
  'failed to fetch', // Chrome
  'networkerror', // Firefox
  'load failed', // Safari
  'fetch failed', // undici (Node)
  'network request failed',
  'err_internet_disconnected',
  'err_network',
  'err_name_not_resolved',
  'err_connection',
  'socket hang up',
  'econnrefused',
  'econnreset',
  'enotfound',
  'etimedout',
  'authretryablefetcherror', // gotrue-js
  /* Prazo nosso estourado (`AbortSignal.timeout` em cliente.ts). Medido: o postgrest
     NÃO lança — devolve `{error:{message:'TimeoutError: The operation was aborted due
     to timeout'}, status:0}`. Nenhuma marca acima casava, então o prazo virava erro
     TERMINAL em vez de "tente de novo".
     As duas marcas são específicas de PRAZO: um cancelamento pedido por alguém diz
     "The operation was aborted" SEM "due to timeout", e continua não sendo queda. */
  'timeouterror',
  'aborted due to timeout',
];

/**
 * `true` quando a chamada não chegou a ser respondida pelo banco — sem rede, DNS
 * quebrado, projeto fora do ar, gateway em 5xx. É o que vira "offline" e mantém o
 * rascunho na fila.
 *
 * O que NÃO é falha de conexão, de propósito: 401/403 (permissão — o banco decidiu),
 * 409 (conflito de versão), 4xx em geral e `AbortError` (alguém cancelou). Tratar
 * esses como queda esconderia o problema real atrás de um "sem conexão" mentiroso.
 */
export function ehFalhaDeConexao(erro: unknown): boolean {
  if (erro === null || erro === undefined) return false;
  const o = erro as { name?: unknown; status?: unknown; code?: unknown; codigo?: unknown };
  /* Quem passa por `traduzirErro` chega aqui como ErroFincast: sem `status`, sem `code`
     e com mensagem em PT-BR que não casa com nenhuma marca de rede. Sem esta linha, a
     máquina de offline ficava INERTE para o rascunho — nunca transitava para 'offline',
     nunca contava falhas seguidas, e a retentativa só sobrevivia por acidente porque
     havia pendência. Comparamos o campo (não `instanceof`) para não depender de haver
     uma única instância do módulo. */
  if (o.codigo === 'SEM_CONEXAO') return true;
  if (o.name === 'AbortError') return false;
  if (typeof o.status === 'number') return o.status >= 500 || o.status === 0 || o.status === 408;
  if (o.code === '57014' || o.code === '08006' || o.code === '08003') return true; // postgres
  const texto = textoDoErro(erro).toLowerCase();
  return MARCAS_DE_REDE.some((m) => texto.includes(m));
}

/**
 * `true` quando a tentativa não chegou a acontecer porque JÁ HÁ uma gravação no ar.
 *
 * Não é falha de nada: é o `rascunho.descarregar()` avisando que outra execução está
 * drenando a fila. Ele precisa lançar (sair em silêncio era lido como sucesso e a barra
 * anunciava "Salvo" com o conteúdo em lugar nenhum), mas quem recebe não pode tratar
 * isso como erro do usuário.
 *
 * Sem esta classificação, o caminho era: servidor lento → a retentativa de 5 s chega com
 * a primeira gravação ainda no ar → `descarregar` lança → nenhuma marca de rede casa →
 * `salvamento = 'erro'` → a barra superior passa a alternar "Salvando…" / "Salvo às
 * HH:MM" / "Não foi possível salvar" a cada ciclo. Foi o único achado que dependia de
 * DUAS pessoas usando ao mesmo tempo, que é justamente quando o servidor fica lento.
 */
export function ehGravacaoEmAndamento(erro: unknown): boolean {
  if (erro === null || erro === undefined) return false;
  return (erro as { codigo?: unknown }).codigo === 'GRAVACAO_EM_ANDAMENTO';
}

/**
 * Erros terminais seguidos antes de parar de tentar sozinho e exigir decisão.
 *
 * Retentar 401 faz sentido (o cliente renova o token e a seguinte passa). Retentar 403 de
 * permissão ou 409 de conflito de versão não passa nunca — e girava de 5 s a 60 s para
 * sempre, em silêncio, porque o critério de parar era `pendencias === 0` e não "este erro
 * tem conserto". Para o usuário: "eu digito e nada acontece", com a tela normal.
 */
const ERROS_TERMINAIS_ATE_DESISTIR = 3;

/* ============================================================================
 * Globais do navegador, lidos com cuidado
 * ========================================================================== */

interface AlvoDeEventos {
  addEventListener(tipo: string, ouvinte: () => void): void;
  removeEventListener(tipo: string, ouvinte: () => void): void;
}

function alvoDeEventos(): AlvoDeEventos | null {
  const g = globalThis as unknown as Partial<AlvoDeEventos>;
  return typeof g.addEventListener === 'function' && typeof g.removeEventListener === 'function'
    ? (g as AlvoDeEventos)
    : null;
}

/** `true`/`false` conforme o navegador; `null` quando o ambiente não informa (Node). */
function navegadorDizOnline(): boolean | null {
  const g = globalThis as unknown as { navigator?: { onLine?: unknown } };
  const valor = g.navigator?.onLine;
  return typeof valor === 'boolean' ? valor : null;
}

/* ============================================================================
 * Monitor
 * ========================================================================== */

export interface MonitorConexao {
  /** Fotografia do estado atual. */
  estado(): EstadoConexao;
  /** Recebe o estado agora e a cada mudança. Devolve a função que cancela. */
  assinar(ouvinte: (estado: EstadoConexao) => void): () => void;
  /**
   * Envolve uma chamada de escrita: marca `salvando`, e termina em `salvo`,
   * `pendente` (sem conexão) ou `erro`. Repassa o valor e RELANÇA a exceção — quem
   * chamou continua responsável pelo próprio tratamento.
   */
  rastrearSalvamento<T>(operacao: () => Promise<T>): Promise<T>;
  /** Quantas alterações locais ainda não subiram. Quem edita informa. */
  definirPendencias(quantidade: number): void;
  /** Uma chamada qualquer deu certo: o servidor responde. */
  registrarSucesso(): void;
  /** Uma chamada qualquer falhou; o monitor classifica o motivo. */
  registrarFalha(erro: unknown): void;
  /**
   * Registra quem sabe subir o rascunho. É chamado sozinho quando a conexão volta e
   * a cada nova tentativa enquanto houver pendência. Devolve a função que cancela.
   */
  aoReconectar(rotina: () => Promise<unknown>): () => void;
  /**
   * Tenta agora. Nunca lança.
   *
   * `porDecisaoDoUsuario` só é `true` quando veio de um clique: além de tentar, ele zera
   * a contagem de erros sem conserto e devolve ao monitor o direito de continuar
   * tentando sozinho.
   */
  sincronizarAgora(porDecisaoDoUsuario?: boolean): Promise<void>;
  /** Solta os ouvintes de janela e o cronômetro. Para teste e desmontagem. */
  encerrar(): void;
}

export function criarMonitorConexao(opcoes: OpcoesMonitor = {}): MonitorConexao {
  const limite = Math.max(1, opcoes.falhasParaOffline ?? 1);
  const atrasos = opcoes.atrasos ?? ATRASOS_PADRAO;
  const agora = opcoes.agora ?? (() => new Date());

  let rede: SituacaoRede = navegadorDizOnline() === false ? 'offline' : 'online';
  let salvamento: SituacaoSalvamento = 'ocioso';
  let pendencias = 0;
  let salvoEm: Date | null = null;
  let erro: string | null = null;

  let falhasSeguidas = 0;
  let tentativa = 0;
  let cronometro: ReturnType<typeof setTimeout> | null = null;
  let sincronizando = false;
  /** Erros que repetir não resolve, em sequência. Zera em qualquer sucesso. */
  let errosTerminais = 0;

  const ouvintes = new Set<(estado: EstadoConexao) => void>();
  const rotinas = new Set<() => Promise<unknown>>();
  let ultimo: EstadoConexao | null = null;

  function hora(d: Date): string {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  function frase(): string {
    if (salvamento === 'erro') return `Não foi possível salvar: ${erro ?? 'erro desconhecido'}`;
    if (rede === 'offline') {
      return pendencias > 0 || salvamento === 'pendente'
        ? 'Sem conexão. Pode continuar editando — os cálculos rodam aqui no navegador e o rascunho sobe sozinho quando a conexão voltar.'
        : 'Sem conexão. Pode continuar editando; nada será perdido.';
    }
    if (salvamento === 'salvando') return 'Salvando…';
    if (salvamento === 'pendente') return 'Alterações ainda não salvas. Tentando de novo…';
    if (salvamento === 'salvo' && salvoEm !== null) return `Salvo às ${hora(salvoEm)}`;
    return pendencias > 0 ? 'Alterações não salvas.' : 'Tudo salvo.';
  }

  function montar(): EstadoConexao {
    return { rede, salvamento, pendencias, salvoEm, erro, mensagem: frase() };
  }

  function igual(a: EstadoConexao, b: EstadoConexao): boolean {
    return (
      a.rede === b.rede &&
      a.salvamento === b.salvamento &&
      a.pendencias === b.pendencias &&
      a.salvoEm?.getTime() === b.salvoEm?.getTime() &&
      a.erro === b.erro
    );
  }

  function emitir(): void {
    const atual = montar();
    if (ultimo !== null && igual(ultimo, atual)) return;
    ultimo = atual;
    for (const o of ouvintes) {
      try {
        o(atual);
      } catch {
        // ouvinte quebrado não pode derrubar os outros nem o salvamento
      }
    }
  }

  /* ---------- cronômetro de nova tentativa ---------- */

  function pararCronometro(): void {
    if (cronometro !== null) {
      clearTimeout(cronometro);
      cronometro = null;
    }
  }

  function agendar(): void {
    pararCronometro();
    if (rotinas.size === 0) return;
    if (salvamento !== 'pendente' && pendencias === 0) return;
    /* Desistir de tentar sozinho não é desistir do trabalho: a fila continua em memória e
       o conteúdo continua na tela. O que para é o giro invisível — daqui em diante quem
       decide é o usuário, por `sincronizarAgora()`. */
    if (errosTerminais >= ERROS_TERMINAIS_ATE_DESISTIR) return;
    const espera = atrasos[Math.min(tentativa, atrasos.length - 1)] ?? 60_000;
    tentativa += 1;
    cronometro = setTimeout(() => {
      cronometro = null;
      void sincronizarAgora();
    }, espera);
  }

  /* ---------- transições ---------- */

  function ficouOnline(): void {
    const mudou = rede !== 'online';
    rede = 'online';
    falhasSeguidas = 0;
    if (mudou) {
      tentativa = 0;
      emitir();
      void sincronizarAgora(); // a conexão voltou: sobe o rascunho sozinho
      return;
    }
    emitir();
  }

  function ficouOffline(): void {
    if (rede === 'offline') {
      emitir();
      return;
    }
    rede = 'offline';
    if (salvamento === 'salvando') salvamento = 'pendente';
    emitir();
    agendar();
  }

  function registrarSucesso(): void {
    ficouOnline();
  }

  function registrarFalha(e: unknown): void {
    if (ehFalhaDeConexao(e)) {
      falhasSeguidas += 1;
      if (falhasSeguidas >= limite) ficouOffline();
      else emitir();
      return;
    }
    // o servidor respondeu — logo, há conexão; o problema é outro
    falhasSeguidas = 0;
    rede = 'online';
    emitir();
  }

  async function sincronizarAgora(porDecisaoDoUsuario = false): Promise<void> {
    /* Clicar em "tentar de novo" é uma decisão nova: zera a contagem de erros sem
       conserto e devolve ao monitor o direito de tentar sozinho. */
    if (porDecisaoDoUsuario) errosTerminais = 0;
    /* A trava fica nos DOIS lugares de propósito: `agendar()` para o cronômetro, e esta
       linha para quem já tinha um disparo a caminho — ou qualquer outro caminho automático
       que chegue aqui. Sem ela, parar de agendar não seria o mesmo que parar de tentar. */
    else if (errosTerminais >= ERROS_TERMINAIS_ATE_DESISTIR) return;
    if (sincronizando || rotinas.size === 0) return;
    sincronizando = true;
    try {
      for (const rotina of [...rotinas]) {
        await rotina();
      }
      falhasSeguidas = 0;
      rede = 'online';
      tentativa = 0;
      /* NÃO promovemos para 'salvo' aqui.
         Uma rotina pode voltar sem ter escrito nada, e anunciar "Salvo às 10:15"
         nesse caso é mentira — foi medido: `{salvamento:'salvo', pendencias:1}` com
         zero idas ao servidor. Quem confirma gravação é `rastrearSalvamento`, que só
         marca 'salvo' DEPOIS de a operação devolver.
         Aqui limpamos o erro e, se não sobrou pendência, saímos de 'erro'/'pendente'
         para 'ocioso' — antes não havia como SAIR de 'erro'. */
      erro = null;
      errosTerminais = 0;
      if (pendencias === 0) {
        if (salvamento === 'pendente' || salvamento === 'erro') salvamento = 'ocioso';
      } else {
        agendar(); // ainda há trabalho: a retentativa não pode morrer aqui
      }
      emitir();
    } catch (e) {
      if (ehGravacaoEmAndamento(e)) {
        /* Não é falha: outra execução está drenando a mesma fila. Nem offline, nem erro,
           nem frase nova na barra — só esperar o `finally` reagendar. Marcar 'erro' aqui
           era o que fazia a barra piscar entre "Salvando…" e "Não foi possível salvar". */
      } else if (ehFalhaDeConexao(e)) {
        errosTerminais = 0; // rede volta; isto não conta como "não tem conserto"
        salvamento = 'pendente';
        ficouOffline();
      } else {
        errosTerminais += 1;
        salvamento = 'erro';
        erro = textoDoErro(e) || 'erro desconhecido';
        rede = 'online';
        emitir();
      }
    } finally {
      sincronizando = false;
      agendar();
    }
  }

  /* ---------- ouvintes de janela ---------- */

  const janela = alvoDeEventos();
  const aoOffline = (): void => ficouOffline();
  const aoOnline = (): void => {
    // pista, não veredito: só uma chamada bem-sucedida confirma que voltou
    tentativa = 0;
    if (rotinas.size > 0) void sincronizarAgora();
    else ficouOnline();
  };
  if (janela !== null) {
    janela.addEventListener('offline', aoOffline);
    janela.addEventListener('online', aoOnline);
  }

  /* ---------- interface pública ---------- */

  return {
    estado: montar,

    assinar(ouvinte) {
      ouvintes.add(ouvinte);
      ouvinte(montar()); // já entrega o estado atual: a tela nasce correta
      return () => {
        ouvintes.delete(ouvinte);
      };
    },

    async rastrearSalvamento(operacao) {
      salvamento = 'salvando';
      erro = null;
      emitir();
      try {
        const valor = await operacao();
        salvamento = 'salvo';
        salvoEm = agora();
        pendencias = 0;
        erro = null;
        errosTerminais = 0;
        registrarSucesso();
        return valor;
      } catch (e) {
        if (ehFalhaDeConexao(e)) {
          salvamento = 'pendente';
          registrarFalha(e); // vira offline e agenda a nova tentativa
        } else {
          salvamento = 'erro';
          erro = textoDoErro(e) || 'erro desconhecido';
          registrarFalha(e);
        }
        throw e;
      }
    },

    definirPendencias(quantidade) {
      const n = Math.max(0, Math.trunc(quantidade));
      if (n === pendencias) return;
      pendencias = n;
      if (n > 0 && salvamento === 'salvo') salvamento = 'ocioso';
      emitir();
      if (n > 0) agendar();
      else if (salvamento !== 'pendente') pararCronometro();
    },

    registrarSucesso,
    registrarFalha,

    aoReconectar(rotina) {
      rotinas.add(rotina);
      // `agendar()` não faz nada quando ainda não há rotina registrada. Se a queda
      // aconteceu ANTES desta chamada (ou a tela que sabe subir o rascunho montou
      // depois), a fila ficaria parada para sempre — por isso reagenda aqui.
      agendar();
      return () => {
        rotinas.delete(rotina);
      };
    },

    sincronizarAgora,

    encerrar() {
      pararCronometro();
      if (janela !== null) {
        janela.removeEventListener('offline', aoOffline);
        janela.removeEventListener('online', aoOnline);
      }
      ouvintes.clear();
      rotinas.clear();
    },
  };
}

/**
 * Monitor único do app. Um por página: o estado de conexão é global, e dois
 * monitores dariam duas respostas diferentes na mesma tela. Em teste, use
 * `criarMonitorConexao()` para ter uma instância isolada.
 */
export const conexao: MonitorConexao = criarMonitorConexao();
